// src/config/database.js

// Browser check
const isBrowser = typeof window !== 'undefined';

// Create a basic logger immediately available
let debugLogger = {
    info: (...args) => console.log('[DEBUG]', ...args),
    error: (...args) => console.error('[DEBUG]', ...args)
};

// Initialize empty pool and functions for browser environment
let pool = null;
let testConnection = async () => false;
let query = async () => [[]];
let getConnection = async () => null;

// Only setup database on server side
if (!isBrowser) {
    // Dynamic imports and setup in an async IIFE
    (async () => {
        try {
            // Load environment variables server-side only
            const dotenvModule = await import('dotenv');
            dotenvModule.config();

            // Dynamic import for URL parsing
            const { default: url } = await import('url');

            let dbConfig;

            // If DATABASE_URL is available (Heroku), parse it
            if (process.env.DATABASE_URL) {
              const params = url.parse(process.env.DATABASE_URL);
              const auth = params.auth.split(':');
              
              dbConfig = {
                host: params.hostname,
                port: params.port,
                user: auth[0],
                password: auth[1],
                database: params.pathname.slice(1),
                ssl: { rejectUnauthorized: false },
                source: 'DATABASE_URL'
              };
            } else {
              // Use individual environment variables
              let sslConfig = false;
              
              // If DB_SSL is explicitly set to true, or if we have SSL certificate, enable SSL
              if (process.env.DB_SSL === 'true' || process.env.MYSQL_SSL_CA) {
                sslConfig = {
                  rejectUnauthorized: false
                };
                
                // If we have a CA certificate, use it
                if (process.env.MYSQL_SSL_CA) {
                  sslConfig.ca = process.env.MYSQL_SSL_CA.replace(/\\n/g, '\n');
                }
              }
              
              dbConfig = {
                host: process.env.DB_HOST,
                port: process.env.DB_PORT,
                user: process.env.DB_USER,
                password: process.env.DB_PASSWORD,
                database: process.env.DB_NAME,
                ssl: sslConfig,
                source: 'env_vars'
              };
            }

            debugLogger.info('DATABASE', 'Creating database pool with the following parameters', {
                host: dbConfig.host,
                port: dbConfig.port,
                database: dbConfig.database,
                ssl: !!dbConfig.ssl,
                source: dbConfig.source
            });

            // Only import mysql2 on server side
            const mysql = await import('mysql2/promise');
            
            // Create connection pool with enhanced configuration
            pool = mysql.createPool({
                host: dbConfig.host,
                port: parseInt(dbConfig.port) || 3306,
                user: dbConfig.user,
                password: dbConfig.password,
                database: dbConfig.database,
                ssl: dbConfig.ssl,
                waitForConnections: true,
                connectionLimit: 10,
                queueLimit: 0,
                acquireTimeout: 30000,
                timeout: 30000,
                enableKeepAlive: true,
                keepAliveInitialDelay: 0,
                reconnect: true
            });

            debugLogger.info('DATABASE', 'Created database pool');

            // Test connection function
            testConnection = async () => {
                try {
                    const connection = await pool.getConnection();
                    await connection.ping();
                    connection.release();
                    debugLogger.info('DATABASE', 'Successfully connected to database', { timestamp: new Date() });
                    return true;
                } catch (error) {
                    debugLogger.error('DATABASE', 'Database connection failed', error);
                    return false;
                }
            };

            // Enhanced query function with detailed logging
            query = async (text, params = []) => {
                const startTime = Date.now();
                try {
                    const [rows] = await pool.execute(text, params);
                    const duration = Date.now() - startTime;
                    
                    debugLogger.info('DATABASE', 'Executed query', {
                        text,
                        duration,
                        rows: Array.isArray(rows) ? rows.length : 0,
                        insertId: rows.insertId
                    });
                    
                    return [rows];
                } catch (error) {
                    const duration = Date.now() - startTime;
                    debugLogger.error('DATABASE', 'Query error', {
                        text,
                        params,
                        duration,
                        error: error.message,
                        stack: error.stack
                    });
                    throw error;
                }
            };

            // Get connection function for transactions
            getConnection = async () => {
                try {
                    return await pool.getConnection();
                } catch (error) {
                    debugLogger.error('DATABASE', 'Failed to get connection', error);
                    throw error;
                }
            };

        } catch (error) {
            console.error('Failed to initialize database:', error);
        }
    })();
}

export { pool, testConnection, query, getConnection, debugLogger };